import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton, minusIconMinus, minusIconDelete } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart minus button is only disabled when quantity is less than 1 (0 or negative)
    // When value is 1, it acts as a delete button and remains enabled
    minusButton.disabled = value < 1;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    // Toggle between minus and trash icon
    if (minusIconMinus && minusIconDelete) {
      if (value === 1) {
        minusIconMinus.classList.add('hidden');
        minusIconDelete.classList.remove('hidden');
      } else {
        minusIconMinus.classList.remove('hidden');
        minusIconDelete.classList.add('hidden');
      }
    }
  }

  /**
   * Handles the quantity decrease event.
   * Overridden in cart to trigger delete/remove if current quantity is 1.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { value } = this.getCurrentValues();
    if (value === 1) {
      this.setValue('0');
      this.onQuantityChange();
      this.updateButtonStates();
    } else {
      super.decreaseQuantity(event);
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
